import { useMemo } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { api, queryClient, type ApiResponse, type ApiBody } from 'config/api'

type ListRoute = 'api.v1.grade_appeals.list'
const GRADE_APPEALS_LIST_QUERY_KEY = 'grade-appeals-list'

export function refreshGradeAppealsList() {
  return queryClient.invalidateQueries({
    queryKey: [GRADE_APPEALS_LIST_QUERY_KEY],
  })
}

/** An appeal plus the section it belongs to (the row carries its own section). */
export type SectionAppealRow = ApiResponse<ListRoute>['appeals'][number]

/** The tab the instructor is on, as the route's body already defines it. */
export type AppealsTab = NonNullable<ApiBody<ListRoute>['status']>

/**
 * One page of appeals across every section the instructor reviews. A single
 * request — the server pages and counts across all sections — rather than the
 * old per-section fan-out.
 */
export function useSectionAppeals({
  sectionIds,
  assignmentId,
  tab,
  page,
  pageSize,
}: {
  sectionIds: Array<string>
  assignmentId?: string
  tab: AppealsTab
  page: number
  pageSize: number
}) {
  // Sorted so the cache key is stable regardless of the order sections stream
  // in from Firestore.
  const sortedIds = useMemo(() => [...sectionIds].sort(), [sectionIds])

  const { data, isLoading, isError, isPlaceholderData } = useQuery({
    queryKey: [
      GRADE_APPEALS_LIST_QUERY_KEY,
      sortedIds,
      assignmentId,
      tab,
      page,
      pageSize,
    ],
    queryFn: async () => {
      const [result, error] = await api.v1.gradeAppeals
        .list({
          body: {
            sectionIds: sortedIds,
            status: tab,
            page,
            pageSize,
            ...(assignmentId === undefined ? {} : { assignmentId }),
          },
        })
        .safe()
      if (error) throw error
      return result
    },
    enabled: sortedIds.length > 0,
    staleTime: 30_000,
    // A failed background refetch shouldn't blank the table — keep the last page.
    placeholderData: keepPreviousData,
  })

  const state = data && !('error' in data) ? data : null

  return {
    rows: state?.appeals ?? [],
    counts: state?.counts ?? {
      pending: 0,
      accepted: 0,
      partiallyAccepted: 0,
      declined: 0,
    },
    total: state?.total ?? 0,
    isLoading: sortedIds.length > 0 && isLoading,
    isError,
    isPlaceholderData,
  }
}
